import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@vd/ui";
import { MessageSquareWarning } from "lucide-react";
import { useMemo, useState } from "react";
import { bugReportsInfinite } from "../../../api/admin";
import type { BugReportRow } from "../../../api/types";
import { requireAdmin, useMe } from "../../../auth/useMe";
import { DataTable } from "../../../components/DataTable";
import { Button, Card, EmptyState, ErrorState, Loading, PageHeader } from "../../../components/ui";
import { fmtDateTime } from "../../../lib/format";
import { useNavigate } from "../../../lib/navigate";
import {
  EmailBadge,
  isResolved,
  KindBadge,
  ReportCode,
  reportCode,
  SourceBadge,
  StatusBadge,
  TranscriptBadge,
} from "./-badges";
import { RESOLUTION_LABEL, ResolveDialog } from "./-resolve";

export const Route = createFileRoute("/admin/support/")({
  staticData: { crumb: { label: "Support", parents: [] } },
  beforeLoad: requireAdmin,
  component: BugReportsPage,
});

const col = createColumnHelper<BugReportRow>();

function BugReportsPage() {
  const navigate = useNavigate();
  // Resolving reports is the ops console's once it is set up (story CA4).
  const triage = useMe().operator_pages === true;
  const q = useInfiniteQuery(bugReportsInfinite);
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<null | "fixed" | "wont_fix" | "not_a_bug">(null);
  const open = rows.filter((r) => !isResolved(r.status));
  const chosen = rows.filter((r) => picked.has(r.id));
  const toggle = (id: string, on: boolean) =>
    setPicked((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  // Rebuilt each render: the selection cells read the current selection.
  const all = [
    col.display({
      id: "pick",
      header: () => (
        <Checkbox
          label="Select every open report"
          checked={
            open.length > 0 && open.every((r) => picked.has(r.id))
              ? true
              : picked.size > 0
                ? "indeterminate"
                : false
          }
          onCheckedChange={(on) => setPicked(on ? new Set(open.map((r) => r.id)) : new Set())}
        />
      ),
      cell: (c) =>
        isResolved(c.row.original.status) ? null : (
          <Checkbox
            label={`Select ${reportCode(c.row.original.number) || "report"}`}
            checked={picked.has(c.row.original.id)}
            onCheckedChange={(on) => toggle(c.row.original.id, on)}
          />
        ),
    }),
    col.accessor("number", {
      header: "Report",
      cell: (c) => {
        const n = c.getValue();
        return n ? <ReportCode number={n} className="text-caption" /> : null;
      },
    }),
    col.accessor("created_at", {
      header: "Sent",
      cell: (c) => <span className="tnum whitespace-nowrap">{fmtDateTime(c.getValue())}</span>,
    }),
    col.accessor((r) => r.user?.name ?? "", { id: "user", header: "User" }),
    col.accessor("excerpt", {
      header: "Problem",
      cell: (c) =>
        c.getValue() ? (
          <span className="line-clamp-1 max-w-[520px] text-ink-2">{c.getValue()}</span>
        ) : (
          <span className="text-ink-3">No description</span>
        ),
    }),
    col.accessor("transcript_status", {
      header: "Source",
      cell: (c) =>
        c.row.original.source === "assistant" ? (
          <span className="flex items-center gap-1.5">
            <SourceBadge severity={c.row.original.severity} kind={c.row.original.report_kind} />
            {c.row.original.report_kind && c.row.original.report_kind !== "bug" ? (
              <KindBadge kind={c.row.original.report_kind} />
            ) : null}
          </span>
        ) : (
          <TranscriptBadge status={c.getValue()} />
        ),
    }),
    col.accessor("status", {
      header: "Status",
      cell: (c) => (
        <span className="flex items-center gap-1.5">
          <StatusBadge
            status={c.getValue()}
            fixedVersion={c.row.original.fixed_version}
            stage={c.row.original.stage ?? null}
          />
          {c.row.original.follow_up_count ? (
            <span className="whitespace-nowrap text-caption text-ink-3 tnum">
              {c.row.original.follow_up_count} {c.row.original.follow_up_count === 1 ? "message" : "messages"}
            </span>
          ) : null}
        </span>
      ),
    }),
    col.accessor("email_status", {
      header: "Email",
      cell: (c) => <EmailBadge status={c.getValue()} />,
    }),
    col.accessor("app_version", {
      header: "Version",
      cell: (c) => <span className="font-mono text-caption text-ink-2">{c.getValue() ?? ""}</span>,
    }),
  ];
  const hidden = (c: (typeof all)[number]) =>
    c.id === "pick" || ("accessorKey" in c && c.accessorKey === "email_status");
  const columns = triage ? all : all.filter((c) => !hidden(c));

  return (
    <>
      <PageHeader
        title="Support"
        actions={
          triage && chosen.length > 0 ? (
            <>
              <span className="text-caption text-ink-2 tnum">{chosen.length} selected</span>
              <Button size="sm" onClick={() => setDialog("not_a_bug")}>
                {RESOLUTION_LABEL.not_a_bug}
              </Button>
              <Button size="sm" onClick={() => setDialog("wont_fix")}>
                {RESOLUTION_LABEL.wont_fix}
              </Button>
              <Button size="sm" variant="primary" onClick={() => setDialog("fixed")}>
                Mark fixed
              </Button>
            </>
          ) : undefined
        }
      />
      {dialog ? (
        <ResolveDialog
          open
          onClose={() => {
            setDialog(null);
            setPicked(new Set());
          }}
          reportIds={chosen.map((r) => r.id)}
          title={chosen.length === 1 ? (chosen[0]?.title ?? null) : null}
          number={chosen.length === 1 ? (chosen[0]?.number ?? null) : null}
          initial={dialog}
        />
      ) : null}
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <>
            <DataTable
              data={rows}
              columns={columns}
              getRowId={(r) => r.id}
              onRowClick={(r) => navigate({ to: "/admin/support/$reportId", params: { reportId: r.id } })}
              rowLabel={(r) =>
                `Problem report from ${r.user?.name ?? "unknown"}, ${fmtDateTime(r.created_at)}`
              }
              empty={
                <EmptyState
                  icon={<MessageSquareWarning />}
                  title="No problem reports"
                  description="Reports sent from the desktop app show up here."
                />
              }
            />
            {q.hasNextPage ? (
              <div className="flex justify-center border-t border-hairline p-3">
                <Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  );
}
